'use client';

/*
 * Documentation:
 * Accordion — https://app.subframe.com/6fc7f5fbb098/library?component=Accordion_d2e81e20-863a-4027-826a-991d8910efd9
 */
import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { FeatherChevronDown } from '@subframe/core';
import * as SubframeCore from '@subframe/core';
import * as SubframeUtils from '../utils';
interface ChevronProps extends React.ComponentProps<typeof SubframeCore.Collapsible.Chevron> {
  className?: string;
}
const Chevron = React.forwardRef<React.ElementRef<typeof FeatherChevronDown>, ChevronProps>(function Chevron(t0, ref) {
  const $ = _c(11);
  let className;
  let otherProps;
  if ($[0] !== t0) {
    ({
      className,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = otherProps;
  } else {
    className = $[1];
    otherProps = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = SubframeUtils.twClassNames("text-body font-body text-default-font", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== ref || $[6] !== t1) {
    t2 = <FeatherChevronDown className={t1} ref={ref} />;
    $[5] = ref;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== otherProps || $[9] !== t2) {
    t3 = <SubframeCore.Collapsible.Chevron {...otherProps}>{t2}</SubframeCore.Collapsible.Chevron>;
    $[8] = otherProps;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
});
interface ContentProps extends React.ComponentProps<typeof SubframeCore.Collapsible.Content> {
  children?: React.ReactNode;
  className?: string;
}
const Content = React.forwardRef<HTMLDivElement, ContentProps>(function Content(t0, ref) {
  const $ = _c(9);
  let children;
  let className;
  let otherProps;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = otherProps;
  } else {
    children = $[1];
    className = $[2];
    otherProps = $[3];
  }
  let t1;
  if ($[4] !== children || $[5] !== className || $[6] !== otherProps || $[7] !== ref) {
    t1 = children ? <SubframeCore.Collapsible.Content asChild={true} {...otherProps}><div className={SubframeUtils.twClassNames("flex w-full flex-col items-start gap-2", className)} ref={ref}>{children}</div></SubframeCore.Collapsible.Content> : null;
    $[4] = children;
    $[5] = className;
    $[6] = otherProps;
    $[7] = ref;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
});
interface TriggerProps extends React.ComponentProps<typeof SubframeCore.Collapsible.Trigger> {
  children?: React.ReactNode;
  className?: string;
}
const Trigger = React.forwardRef<HTMLDivElement, TriggerProps>(function Trigger(t0, ref) {
  const $ = _c(9);
  let children;
  let className;
  let otherProps;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = otherProps;
  } else {
    children = $[1];
    className = $[2];
    otherProps = $[3];
  }
  let t1;
  if ($[4] !== children || $[5] !== className || $[6] !== otherProps || $[7] !== ref) {
    t1 = children ? <SubframeCore.Collapsible.Trigger asChild={true} {...otherProps}><div className={SubframeUtils.twClassNames("flex w-full cursor-pointer flex-col items-start gap-2", className)} ref={ref}>{children}</div></SubframeCore.Collapsible.Trigger> : null;
    $[4] = children;
    $[5] = className;
    $[6] = otherProps;
    $[7] = ref;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
});
interface AccordionRootProps extends React.ComponentProps<typeof SubframeCore.Collapsible.Root> {
  trigger?: React.ReactNode;
  children?: React.ReactNode;
  open?: boolean;
  className?: string;
}
const AccordionRoot = React.forwardRef<HTMLDivElement, AccordionRootProps>(function AccordionRoot(t0, ref) {
  const $ = _c(27);
  let children;
  let className;
  let open;
  let otherProps;
  let t1;
  let trigger;
  if ($[0] !== t0) {
    ({
      trigger,
      children,
      open,
      className,
      defaultOpen: t1,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = open;
    $[4] = otherProps;
    $[5] = t1;
    $[6] = trigger;
  } else {
    children = $[1];
    className = $[2];
    open = $[3];
    otherProps = $[4];
    t1 = $[5];
    trigger = $[6];
  }
  const defaultOpen = t1 === undefined ? false : t1;
  let t2;
  if ($[7] !== className) {
    t2 = SubframeUtils.twClassNames("group/d2e81e20 flex w-full flex-col items-start rounded-md", className);
    $[7] = className;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== trigger) {
    t3 = trigger ? <div className="flex w-full shrink-0 grow basis-0 flex-col items-start group-data-[state=open]/d2e81e20:h-auto group-data-[state=open]/d2e81e20:w-full group-data-[state=open]/d2e81e20:flex-none">{trigger}</div> : null;
    $[9] = trigger;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t3) {
    t4 = <Trigger>{t3}</Trigger>;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== children) {
    t5 = children ? <div className="flex w-full shrink-0 grow basis-0 flex-col items-start">{children}</div> : null;
    $[13] = children;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t5) {
    t6 = <Content>{t5}</Content>;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== ref || $[18] !== t2 || $[19] !== t4 || $[20] !== t6) {
    t7 = <div className={t2} ref={ref}>{t4}{t6}</div>;
    $[17] = ref;
    $[18] = t2;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== defaultOpen || $[23] !== open || $[24] !== otherProps || $[25] !== t7) {
    t8 = <SubframeCore.Collapsible.Root open={open} defaultOpen={defaultOpen} asChild={true} {...otherProps}>{t7}</SubframeCore.Collapsible.Root>;
    $[22] = defaultOpen;
    $[23] = open;
    $[24] = otherProps;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
});
export const Accordion = Object.assign(AccordionRoot, {
  Chevron,
  Content,
  Trigger
});